import { SelectOptionProps } from '@acceso/design-system';

import { MEGABYTES_TO_BYTES } from '@/constants';
import { AudienceProviderEnum, ChannelScopeEnum } from '@/features/broadcasters/types';

type TAudienceProviderConfig = {
  label: string;

  /**
   * File types the provider's parser reads, enforced by the file picker. Kantar
   * and EGM deliver tab-separated text (`open(..., encoding='latin-1')` +
   * `split('\t')` on the backend); IBOPE delivers a workbook, read through
   * openpyxl with an xlrd fallback for legacy BIFF.
   */
  fileAccept: string;

  /** Format named in the file field hint. A file extension, so not translated. */
  fileFormatLabel: string;

  maxFileSize: number;

  /**
   * Whether the provider's file covers a period instead of carrying a date per
   * row. Only those providers are asked for a period start date.
   */
  requiresPeriodStart: boolean;

  /**
   * Applied when the provider is selected, as a convenience. Never a
   * constraint: the operator can change them, and the request carries whatever
   * the fields hold at submit time.
   */
  defaults?: {
    scope: ChannelScopeEnum;
    countryCode: string;
  };
};

const TXT_FILE_ACCEPT = '.txt,text/plain';
const EXCEL_FILE_ACCEPT =
  '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export const AUDIENCE_FILE_MAX_SIZE = 25 * MEGABYTES_TO_BYTES;

export const AUDIENCE_PROVIDER_CONFIG: Record<AudienceProviderEnum, TAudienceProviderConfig> = {
  [AudienceProviderEnum.EGM]: {
    label: 'EGM',
    fileAccept: TXT_FILE_ACCEPT,
    fileFormatLabel: 'txt',
    maxFileSize: AUDIENCE_FILE_MAX_SIZE,
    requiresPeriodStart: true,
  },
  [AudienceProviderEnum.KANTAR]: {
    label: 'Kantar',
    fileAccept: TXT_FILE_ACCEPT,
    fileFormatLabel: 'txt',
    maxFileSize: AUDIENCE_FILE_MAX_SIZE,
    requiresPeriodStart: false,
  },
  [AudienceProviderEnum.IBOPE]: {
    label: 'IBOPE',
    fileAccept: EXCEL_FILE_ACCEPT,
    fileFormatLabel: 'xlsx, xls',
    maxFileSize: AUDIENCE_FILE_MAX_SIZE,
    requiresPeriodStart: false,
    // IBOPE's only feed today is Panamanian national TV, but the provider is
    // expected to serve other countries, so this is a default and not a rule.
    defaults: {
      scope: ChannelScopeEnum.NACIONAL,
      countryCode: 'PA',
    },
  },
};

/** Every audience provider, for the upload form and the sources list filter. */
export const AUDIENCE_PROVIDER_OPTIONS: SelectOptionProps<AudienceProviderEnum>[] = (
  Object.keys(AUDIENCE_PROVIDER_CONFIG) as AudienceProviderEnum[]
).map(provider => ({
  label: AUDIENCE_PROVIDER_CONFIG[provider].label,
  value: provider,
}));

/**
 * The providers the broadcast schedule audiences list works with: both its default
 * request scope and the options of its "Proveedor" filter.
 *
 * The criterion is the shape of the provider's file. A period-based file (`EGM`)
 * carries no datetime per row, so its registries do not describe a single emission
 * and have nothing to attach to a program of the grid — offering EGM there could
 * only ever return an empty list. Today this resolves to `[kantar, ibope]`.
 *
 * Deriving the set keeps a new provider listed the day it enters the catalogue. A
 * provider that had to be excluded for a reason unrelated to its file shape would
 * not fit here, and would call for an explicit flag per provider instead.
 */
export const SCHEDULE_AUDIENCE_PROVIDERS: AudienceProviderEnum[] = (
  Object.keys(AUDIENCE_PROVIDER_CONFIG) as AudienceProviderEnum[]
).filter(provider => !AUDIENCE_PROVIDER_CONFIG[provider].requiresPeriodStart);
